/**
 * Quantity select component
 * Mirrors QuantityInput's cart logic but drives line item updates from a <select> dropdown.
 * Options are populated from JS using inventory-quantity so Liquid only needs to render a
 * placeholder <option> for the current quantity to avoid an empty-select flash pre-upgrade.
 */

import { globalEmitter, emitterProps } from 'helpers/emitter-event'
import { QuantityRule } from '@/components/helpers/cart.types'
import { cart, Cart, setCartLineLoading } from '@/components/helpers/cart'
import { ShopifySectionHelper } from '../helpers/section-render'
import { globalState } from '@/components/helpers/utils'

const MAX_OPTIONS = 10

export class QuantitySelect extends HTMLElement {
  private quantity: number = 1
  private inventoryQuantity: number | null = null
  private inventoryManagement: string | null = null
  private inventoryPolicy: string | null = null
  private quantityRule: QuantityRule | null = null
  private isLoading: boolean = false
  private errorTimeout: NodeJS.Timeout | null = null
  private line: number | null = null

  // Elements
  private quantitySelect: HTMLSelectElement | null = null
  private errorDisplay: HTMLElement | null = null

  constructor() {
    super()
    this.handleSelectChange = this.handleSelectChange.bind(this)
  }

  static get observedAttributes() {
    return ['inventory-quantity', 'inventory-management', 'inventory-policy', 'quantity', 'quantity-rule', 'line']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return

    switch (name) {
      case 'inventory-quantity':
        this.inventoryQuantity = newValue ? parseInt(newValue) : null
        break
      case 'inventory-management':
        this.inventoryManagement = newValue || null
        break
      case 'inventory-policy':
        this.inventoryPolicy = newValue || null
        break
      case 'quantity':
        this.quantity = newValue ? parseInt(newValue) : 1
        this.updateQuantityDisplay()
        break
      case 'quantity-rule':
        try {
          this.quantityRule = newValue ? JSON.parse(newValue) : null
        } catch (e) {
          console.error('Invalid quantity rule format:', e)
        }
        break
      case 'line':
        this.line = newValue ? parseInt(newValue) : null
        break
    }
  }

  connectedCallback() {
    this.setupElements()
    this.populateOptions()
    this.addEventListeners()
  }

  disconnectedCallback() {
    this.removeEventListeners()
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
  }

  private setupElements() {
    this.quantitySelect = this.querySelector('[data-quantity-select]')
    this.errorDisplay = this.querySelector('[data-quantity-error]')

    if (this.quantitySelect && this.quantitySelect.value) {
      this.quantity = parseInt(this.quantitySelect.value) || 1
    }
  }

  /**
   * Resolves the real inventory cap for this line item.
   *
   * Returns `null` (no limit) when inventory is untracked
   * (`inventory-management` empty) or the variant keeps selling when out of
   * stock (`inventory-policy === 'continue'`). Otherwise returns the tracked
   * stock level so genuine deny-policy variants stay capped at real stock.
   */
  private getInventoryLimit(): number | null {
    if (!this.inventoryManagement) return null
    if (this.inventoryPolicy === 'continue') return null
    return this.inventoryQuantity
  }

  private populateOptions() {
    if (!this.quantitySelect) return

    const inventoryLimit = this.getInventoryLimit()

    let max = MAX_OPTIONS
    if (inventoryLimit !== null && inventoryLimit > 0 && inventoryLimit < max) {
      max = inventoryLimit
    }
    if (this.quantity > max) max = this.quantity
    if (max < 1) max = 1

    const fragment = document.createDocumentFragment()
    for (let qty = 1; qty <= max; qty++) {
      const option = document.createElement('option')
      option.value = qty.toString()
      option.textContent = qty.toString()
      if (qty === this.quantity) option.selected = true
      fragment.appendChild(option)
    }

    this.quantitySelect.replaceChildren(fragment)
  }

  private addEventListeners() {
    this.quantitySelect?.addEventListener('change', this.handleSelectChange)
  }

  private removeEventListeners() {
    this.quantitySelect?.removeEventListener('change', this.handleSelectChange)
  }

  private handleSelectChange(e: Event) {
    const select = e.target as HTMLSelectElement
    const newQuantity = parseInt(select.value)
    if (!isNaN(newQuantity)) {
      this.handleQuantityUpdate(newQuantity)
    }
  }

  private validateQuantity(newQuantity: number): boolean {
    // Only enforce the inventory cap on genuine over-stock increases. Untracked
    // and "continue selling" variants have no limit, and removals/decreases
    // (newQuantity <= 0) always pass so oversold items can still be removed.
    const inventoryLimit = this.getInventoryLimit()
    if (newQuantity > 0 && inventoryLimit !== null && newQuantity > inventoryLimit) {
      this.setError('Maximum quantity reached')
      return false
    }

    if (this.quantityRule) {
      if (newQuantity < this.quantityRule.min) {
        this.setError(`Minimum quantity is ${this.quantityRule.min}`)
        return false
      }
      if (this.quantityRule.max && newQuantity > this.quantityRule.max) {
        this.setError(`Maximum quantity is ${this.quantityRule.max}`)
        return false
      }
    }

    return true
  }

  private async handleQuantityUpdate(newQuantity: number) {
    try {
      this.setLoading(true)

      if (!this.validateQuantity(newQuantity)) {
        this.setLoading(false)
        // Reset the select to the previous valid quantity so the UI stays in sync
        this.updateQuantityDisplay()
        return
      }

      if (this.line && cart) {
        Cart.skipNextCartPageRefresh = true
        const data = await cart.changeLineItem(
          {
            line: this.line,
            quantity: newQuantity
          },
          { silent: true }
        )

        if (data.error) {
          this.setError(data.error)
          this.updateQuantityDisplay()
          return
        }

        const changeType = data.items_added.length > 0 ? 'plus' : data.items_removed.length > 0 ? 'minus' : null
        const changedItem =
          data.items_added.length > 0
            ? data.items_added[0]
            : data.items_removed.length > 0
              ? data.items_removed[0]
              : null
        if (changeType && changedItem) {
          this.quantity = this.quantity + (changeType === 'plus' ? changedItem.quantity : -changedItem.quantity)
        }
      }
      await ShopifySectionHelper.fetchAndUpdatePage({
        url: globalState?.routes?.cart_url || '/cart',
        fetchSelectors: ['#cart-items-inner', '#cart-summary'],
        targetSelectors: ['#cart-items-inner', '#cart-summary']
      })
      window.lazyLoadInstance?.update()
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })

      this.dispatchEvent(
        new CustomEvent('quantity-changed', {
          detail: { quantity: newQuantity },
          bubbles: true
        })
      )
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
      this.updateQuantityDisplay()
    } finally {
      this.setLoading(false)
    }
  }

  private setError(message: string) {
    if (this.errorDisplay) {
      this.errorDisplay.textContent = message
      this.errorDisplay.classList.remove('opacity-0', 'max-h-0')
      this.errorDisplay.classList.add('opacity-100', 'max-h-[18px]')
    }

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
    this.errorTimeout = setTimeout(() => this.clearError(), 5000)
  }

  private clearError() {
    if (this.errorDisplay) {
      this.errorDisplay.classList.add('opacity-0', 'max-h-0')
      this.errorDisplay.classList.remove('opacity-100', 'max-h-[18px]')
    }
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading
    setCartLineLoading(this, loading)
  }

  private updateQuantityDisplay() {
    if (this.quantitySelect) {
      this.quantitySelect.value = this.quantity.toString()
    }
  }
}
